import { billingApi, type BillingApi } from '../api/BillingApi'
import type { BillingCheckoutContext } from '../models/billing.types'

import { useEnv } from '@/shared/config'

type Navigate = (url: string) => void

// A checkout leaves the SPA. While the provider decision / Shopify POST is pending, every caller
// must join the SAME attempt: two fast plan clicks must never mint two provider sessions. The
// promise is released after settle so a genuine retry remains possible after a failed request.
let activeCheckout: Promise<void> | null = null

function appendQuery(path: string, params: Record<string, string | number | undefined>): string {
  const query = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') query.set(key, String(value))
  }
  return `${path}?${query.toString()}`
}

export function buildHostedCheckoutUrl(
  apiBaseUrl: string,
  context: Omit<BillingCheckoutContext, 'isShopify'>,
): string {
  const path = context.currentPackageId
    ? `/payment/upgrade-subscription/${context.accountId}`
    : `/payment/checkout/${context.accountId}`
  const relative = appendQuery(path, {
    package_id: context.packageId,
    old_package_id: context.currentPackageId ?? undefined,
    workspace_id: context.workspaceId,
    offer_code: context.offerCode,
  })
  return `${apiBaseUrl.replace(/\/$/, '')}${relative}`
}

export class BillingCheckoutFlow {
  constructor(
    private readonly api: Pick<BillingApi, 'createShopifyCheckout'> = billingApi,
    private readonly navigate: Navigate = (url) => window.location.assign(url),
    private readonly apiBaseUrl: () => string = () => useEnv().API_APP_URL,
  ) {}

  execute(context: BillingCheckoutContext): Promise<void> {
    if (activeCheckout) return activeCheckout

    const pending = this.start(context)
    activeCheckout = pending
    const release = (): void => {
      if (activeCheckout === pending) activeCheckout = null
    }
    void pending.then(release, release)
    return pending
  }

  private async start(context: BillingCheckoutContext): Promise<void> {
    const usesShopify =
      context.currentPackageId === 7 || (context.isShopify && context.currentPackageId === null)

    if (usesShopify) {
      if (context.workspaceId === undefined) {
        throw new Error('Shopify checkout requires a selected workspace')
      }
      const result = await this.api.createShopifyCheckout(
        context.workspaceId,
        context.packageId,
        context.offerCode,
      )
      this.navigate(result.confirmation_url)
      return
    }

    this.navigate(buildHostedCheckoutUrl(this.apiBaseUrl(), context))
  }
}
